// src/timing/units.js

export function toInternalDistanceLinear(v, units) {
    const unit = units.distanceLinear;
    return unit === "m" ? v * 1000 : v;
}

export function fromInternalDistanceLinear(v, units) {
    const unit = units.distanceLinear;
    return unit === "m" ? v / 1000 : v;
}

export function fromInternalAccelLinear(v, units) {
    const unit = units.accelLinear;
    if (unit === "G") return v / (9.80665 * 1000);
    return v / 1000; // mm/s2 -> m/s2
}

export function toInternalAccelLinear(v, units) {
    const unit = units.accelLinear;
    if (unit === "G") return v * 9.80665 * 1000;
    return v * 1000; // m/s2 -> mm/s2
}

export function toInternalSpeedLinear(v, units) {
    const unit = units.speedLinear;
    return unit === "m/s" ? v * 1000 : v;
}

export function fromInternalSpeedLinear(v, units) {
    const unit = units.speedLinear;
    return unit === "m/s" ? v / 1000 : v;
}

export function toInternalDistanceLinearByUnit(v, unit) {
    return unit === "m" ? v * 1000 : v;
}

export function fromInternalDistanceLinearByUnit(v, unit) {
    return unit === "m" ? v / 1000 : v;
}

export function toInternalAccelLinearByUnit(v, unit) {
    if (unit === "G") return v * 9.80665 * 1000;
    return v * 1000;
}

export function fromInternalAccelLinearByUnit(v, unit) {
    if (unit === "G") return v / (9.80665 * 1000);
    return v / 1000;
}

export function toInternalSpeedLinearByUnit(v, unit) {
    return unit === "m/s" ? v * 1000 : v;
}

export function fromInternalSpeedLinearByUnit(v, unit) {
    return unit === "m/s" ? v / 1000 : v;
}

export function convertProjectLinearUnits(project, kind, nextUnit) {
    let currentUnit;
    let toInternal;
    let fromInternal;
    let fields;
    if (kind === "distance") {
        currentUnit = project.units.distanceLinear;
        if (currentUnit === nextUnit) return;
        toInternal = (v) => toInternalDistanceLinearByUnit(v, currentUnit);
        fromInternal = (v) => fromInternalDistanceLinearByUnit(v, nextUnit);
        fields = ["distance"];
        project.units.distanceLinear = nextUnit;
    } else if (kind === "accel") {
        currentUnit = project.units.accelLinear;
        if (currentUnit === nextUnit) return;
        toInternal = (v) => toInternalAccelLinearByUnit(v, currentUnit);
        fromInternal = (v) => fromInternalAccelLinearByUnit(v, nextUnit);
        fields = ["accel", "decel"];
        project.units.accelLinear = nextUnit;
    } else if (kind === "speed") {
        currentUnit = project.units.speedLinear;
        if (currentUnit === nextUnit) return;
        toInternal = (v) => toInternalSpeedLinearByUnit(v, currentUnit);
        fromInternal = (v) => fromInternalSpeedLinearByUnit(v, nextUnit);
        fields = ["maxSpeed"];
        project.units.speedLinear = nextUnit;
    } else {
        return;
    }

    for (const axis of project.axes) {
        if (axis.motionType !== "linear") continue;
        if (kind === "speed" && typeof axis.axisMaxSpeed === "number" && isFinite(axis.axisMaxSpeed)) {
            axis.axisMaxSpeed = fromInternal(toInternal(axis.axisMaxSpeed));
        }
        for (const motion of axis.motions) {
            for (const field of fields) {
                if (typeof motion[field] === "number" && isFinite(motion[field])) {
                    motion[field] = fromInternal(toInternal(motion[field]));
                }
            }
        }
    }
}

export const CYLINDER_PRESETS = {
    slow: { label: "遅い", maxSpeed: 100, accel: 0.1 * 9.80665 * 1000 },
    normal: { label: "普通", maxSpeed: 200, accel: 0.2 * 9.80665 * 1000 },
    fast: { label: "速い", maxSpeed: 300, accel: 0.3 * 9.80665 * 1000 }
};

export function applyCylinderPreset(motion, presetKey, units) {
    const preset = CYLINDER_PRESETS[presetKey];
    if (!preset) return;
    motion.accel = fromInternalAccelLinear(preset.accel, units);
    motion.decel = fromInternalAccelLinear(preset.accel, units);
    motion.maxSpeed = fromInternalSpeedLinear(preset.maxSpeed, units);
}

export function detectCylinderPreset(axis, motion, units) {
    if (axis.motionType !== "linear") return "";
    if (typeof motion.accel !== "number" || !isFinite(motion.accel) || motion.accel <= 0) return "";
    if (typeof motion.decel !== "number" || !isFinite(motion.decel) || motion.decel <= 0) return "";
    if (typeof motion.maxSpeed !== "number" || !isFinite(motion.maxSpeed) || motion.maxSpeed <= 0) return "";
    const accel = toInternalAccelLinear(motion.accel, units);
    const decel = toInternalAccelLinear(motion.decel, units);
    const maxSpeed = toInternalSpeedLinear(motion.maxSpeed, units);
    for (const [key, preset] of Object.entries(CYLINDER_PRESETS)) {
        if (Math.abs(accel - preset.accel) < 1e-6 &&
            Math.abs(decel - preset.accel) < 1e-6 &&
            Math.abs(maxSpeed - preset.maxSpeed) < 1e-6) {
            return key;
        }
    }
    return "";
}

export function toInternalAngleRotary(v, units) {
    const unit = units?.angleRotary || "deg";
    return unit === "rad" ? v * 180 / Math.PI : v;
}

export function fromInternalAngleRotary(v, units) {
    const unit = units?.angleRotary || "deg";
    return unit === "rad" ? v * Math.PI / 180 : v;
}

export function toInternalAccelRotary(v, units) {
    const unit = units?.accelRotary || "deg/s2";
    return unit === "rad/s2" ? v * 180 / Math.PI : v;
}

export function fromInternalAccelRotary(v, units) {
    const unit = units?.accelRotary || "deg/s2";
    return unit === "rad/s2" ? v * Math.PI / 180 : v;
}

export function toInternalSpeedRotary(v, units) {
    const unit = units?.speedRotary || "deg/s";
    return unit === "rad/s" ? v * 180 / Math.PI : v;
}

export function fromInternalSpeedRotary(v, units) {
    const unit = units?.speedRotary || "deg/s";
    return unit === "rad/s" ? v * Math.PI / 180 : v;
}

export function toInternalAngleRotaryByUnit(v, unit) {
    return unit === "rad" ? v * 180 / Math.PI : v;
}

export function fromInternalAngleRotaryByUnit(v, unit) {
    return unit === "rad" ? v * Math.PI / 180 : v;
}

export function toInternalAccelRotaryByUnit(v, unit) {
    return unit === "rad/s2" ? v * 180 / Math.PI : v;
}

export function fromInternalAccelRotaryByUnit(v, unit) {
    return unit === "rad/s2" ? v * Math.PI / 180 : v;
}

export function toInternalSpeedRotaryByUnit(v, unit) {
    return unit === "rad/s" ? v * 180 / Math.PI : v;
}

export function fromInternalSpeedRotaryByUnit(v, unit) {
    return unit === "rad/s" ? v * Math.PI / 180 : v;
}

export function convertProjectRotaryUnits(project, kind, nextUnit) {
    let currentUnit;
    let toInternal;
    let fromInternal;
    let fields;
    if (kind === "angle") {
        currentUnit = project.units.angleRotary || "deg";
        if (currentUnit === nextUnit) return;
        toInternal = (v) => toInternalAngleRotaryByUnit(v, currentUnit);
        fromInternal = (v) => fromInternalAngleRotaryByUnit(v, nextUnit);
        fields = ["distance"];
        project.units.angleRotary = nextUnit;
    } else if (kind === "accel") {
        currentUnit = project.units.accelRotary || "deg/s2";
        if (currentUnit === nextUnit) return;
        toInternal = (v) => toInternalAccelRotaryByUnit(v, currentUnit);
        fromInternal = (v) => fromInternalAccelRotaryByUnit(v, nextUnit);
        fields = ["accel", "decel"];
        project.units.accelRotary = nextUnit;
    } else if (kind === "speed") {
        currentUnit = project.units.speedRotary || "deg/s";
        if (currentUnit === nextUnit) return;
        toInternal = (v) => toInternalSpeedRotaryByUnit(v, currentUnit);
        fromInternal = (v) => fromInternalSpeedRotaryByUnit(v, nextUnit);
        fields = ["maxSpeed"];
        project.units.speedRotary = nextUnit;
    } else {
        return;
    }

    for (const axis of project.axes) {
        if (axis.motionType !== "rotary") continue;
        if (kind === "speed" && typeof axis.axisMaxSpeed === "number" && isFinite(axis.axisMaxSpeed)) {
            axis.axisMaxSpeed = fromInternal(toInternal(axis.axisMaxSpeed));
        }
        for (const motion of axis.motions) {
            for (const field of fields) {
                if (typeof motion[field] === "number" && isFinite(motion[field])) {
                    motion[field] = fromInternal(toInternal(motion[field]));
                }
            }
        }
    }
}
